import React from 'react';
import PropTypes from 'prop-types';
import { trackEvent } from 'ddc-track-event';
import { setClassNames } from '@ddc/classnames-js';
import { usePrefs, useLabels, useRequestData } from 'wsm-common-data';

import InventoryCount from './InventoryCount';

import { ModelImage } from '../ModelImage';
import { useGetModelTitle } from '../../hooks/useGetModelTitle';
import { getFormattedPrice } from '../../utils/getFormattedPrice';

/**
 * Row ("jellybean") card — the one net-new card look for the drawer/mega
 * "Shop by Model" surfaces (Cadillac design). Name/content flex on the left,
 * a fixed 144×96 thumbnail is pinned on the right (`object-fit: contain`),
 * separated by an 8px gap; rows are divided by a 1px `--drawer-divider` line.
 *
 * It reuses the same shared sub-components as the other looks — `getModelTitle`,
 * `ModelImage`, `InventoryCount`, `getFormattedPrice` — so title/image/price/
 * inventory logic isn't duplicated (only the layout differs).
 *
 * The root element is a non-`<li>` anchor carrying the `ms-row-card` class: the
 * selector contract the drawer's hover/active SCSS (`.ws-nav-drawer__fragment
 * .ms-row-card`) hooks onto, and non-`<li>` so it dodges the drawer's
 * `_fragment.scss` rule that transparents `<li>` hover/active states.
 */
const RowCard = ({ modelObject, modelLink }) => {
	const prefs = usePrefs();
	const labels = useLabels();
	const { locale, widgetName, windowId } = useRequestData();

	const { image, model, modelInv, gvBodyStyle } = modelObject;
	const {
		modelTitleAttributes,
		startingAtPriceLabel,
		showPricing,
		includeDestinationChargeInPrice,
		showDivider
	} = prefs;

	const getModelTitle = useGetModelTitle();
	const modelTitle = getModelTitle(modelTitleAttributes, modelObject);
	const formattedPrice = getFormattedPrice(
		locale,
		includeDestinationChargeInPrice === 'true',
		modelObject
	);

	const priceClassNames = [
		'price',
		'ms-row-card__price',
		'ddc-font-size-small',
		'd-flex',
		'justify-content-start'
	];
	// Interim: same theme-aware size on both drawer and mega surfaces. A
	// follow-up should make this depend on the row card's actual rendered
	// container width, not device type, so mega can use a smaller class
	// than the drawer -- deferred past US2007779 (out of scope here).
	const titleClassNames = ['ms-row-card__title', 'ddc-font-size-small'];
	const rowCardClassNames = [
		'ms-row-card',
		'd-flex',
		'align-items-center',
		'justify-content-between',
		showDivider === 'true' ? 'ms-row-card--divider' : null
	];

	const trackClick = () => {
		trackEvent(widgetName, windowId, {
			action: 'clicked',
			element: 'Model Image',
			elementCTA: modelTitle,
			result: 'Navigated to model listings'
		});
	};

	return (
		<a
			href={modelLink}
			onClick={trackClick}
			className={setClassNames(rowCardClassNames)}
			data-testid="model-card-row-component-test"
		>
			<div className="ms-row-card__content d-flex flex-column">
				<span className={setClassNames(titleClassNames)}>
					{modelTitle}
				</span>
				{showPricing === 'true' && (
					<span className={setClassNames(priceClassNames)}>
						{`${labels.get(startingAtPriceLabel)} `}{' '}
						{formattedPrice}
					</span>
				)}
				<InventoryCount
					model={model || modelInv}
					bodyStyle={gvBodyStyle}
				/>
			</div>
			<div className="ms-row-card__image">
				<ModelImage url={image} altImage={modelTitle} />
			</div>
		</a>
	);
};

RowCard.propTypes = {
	modelObject: PropTypes.shape({
		year: PropTypes.number,
		make: PropTypes.string,
		model: PropTypes.string,
		modelInv: PropTypes.string,
		gvBodyStyle: PropTypes.string,
		image: PropTypes.string,
		inventoryCount: PropTypes.number,
		minStartingAtPrice: PropTypes.number
	}).isRequired,
	modelLink: PropTypes.string.isRequired
};

export default RowCard;
